import { Space } from 'antd';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { enforceEdgeConstraints, updateShapeByEdge } from '@canvas-common';
import { DEFAULT_SIDE_ITEMS, EDGE_LENGTH_INPUT_LIMIT } from '@common/constants';
import { SideItemOption, SideItemType } from '@common/enums';
import { ArrowInfo, DrawingShape, SideItemInfoModel } from '@common/models';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch } from '@redux/hooks';
import { addClearedShapeEditNotification, setNeedRedrawCanvas } from '@redux/slices/drawingShapeSlice';

import SideItemRadio from '../SideItemRadio/SideItemRadio';
import { InputBoxCustom } from '../inputBoxCustom/inputBoxCustom';

export interface SideItemsRadioListProps {
    shape: DrawingShape;
    arrowInfo: ArrowInfo;
    waterfallHeight: number | undefined;
    backsplashHeight: number | undefined;
    sideOptionType: SideItemOption | undefined;
    selectedSideItemType: SideItemType | undefined;
}

const TypeToTranslationMap = new Map<SideItemType, string>([
    [SideItemType.WALL_SIDE, 'SHAPE_TAB.OPTIONS.WALL_SIDE'],
    [SideItemType.APPLIANCE_SIDE, 'SHAPE_TAB.OPTIONS.APPLIANCE_SIDE'],
    [SideItemType.WATERFALL, 'SHAPE_TAB.OPTIONS.WATERFALL'],
    [SideItemType.END_CAP, 'SHAPE_TAB.OPTIONS.END_CAP'],
    [SideItemType.END_SPLASH, 'SHAPE_TAB.OPTIONS.END_SPLASH'],
]);

const SideItemsRadioList = ({
    shape,
    arrowInfo,
    sideOptionType: _sideOptionType,
    backsplashHeight,
    waterfallHeight,
    selectedSideItemType,
}: SideItemsRadioListProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { drawingShapes, updateDrawingShapes, activeDrawingShape } = useContext(DrawingShapeContext);
    const { selectedValue: selectedMaterial, isLaminateSelected } = useSelectedMaterial();

    const getLabelAndValueFromType = (sideType: SideItemType | undefined): { label: string; value: string } => {
        const base = sideType ? TypeToTranslationMap.get(sideType) : 'UNKNOWN_TYPE';
        return { label: t(`${base}.LABEL`), value: t(`${base}.VALUE`) };
    };

    const updateArrowSize = (arrow: ArrowInfo, value: number) => {
        if (!activeDrawingShape) {
            // shouldn't happen, but avoids a type error (see below)
            return;
        }

        const adjustedValue = value ? enforceEdgeConstraints({ arrow, value, shape }) : arrow.size;
        const newArrow = { ...arrow, size: adjustedValue };
        updateShapeByEdge(newArrow, shape, false);
        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));

        dispatch(addClearedShapeEditNotification(activeDrawingShape.id));
    };

    const displayedSideItems = useMemo(() => {
        const disabled = shape.drawingEdges.some(
            (edge) => edge.id === arrowInfo.drawingEdgeId && !!edge.customSideType
        );
        return DEFAULT_SIDE_ITEMS.filter(
            (item) => !item.materialFilter || !!selectedMaterial?.description?.match(item.materialFilter)
        ).map((item) => {
            const checked = item.sideType === selectedSideItemType;
            const wallOptions =
                checked && item.sideType === SideItemType.WALL_SIDE
                    ? {
                          backsplash: {
                              checked: !!backsplashHeight,
                              height: backsplashHeight,
                          },
                      }
                    : {};
            const waterfallOptions =
                checked && item.sideType === SideItemType.WATERFALL ? { waterfallHeight: waterfallHeight } : {};
            return {
                ...item,
                ...getLabelAndValueFromType(item.sideType),
                arrowInfo,
                disabled,
                checked,
                ...wallOptions,
                ...waterfallOptions,
            } as SideItemInfoModel;
        });
    }, [
        arrowInfo,
        backsplashHeight,
        getLabelAndValueFromType,
        selectedMaterial?.description,
        selectedSideItemType,
        shape.drawingEdges,
        waterfallHeight,
    ]);

    return (
        <div className="side-item" key={arrowInfo.label}>
            <div className="side-title">{arrowInfo.label}</div>
            <InputBoxCustom
                shapeId={activeDrawingShape?.id}
                size={arrowInfo.size ?? EDGE_LENGTH_INPUT_LIMIT.MIN}
                arrowInfo={arrowInfo}
                onUpdatedValue={(value, shapeId) => {
                    // only update if we're still on the same shape
                    if (!!shapeId && shapeId === activeDrawingShape?.id) {
                        updateArrowSize(arrowInfo, value);
                    }
                }}
            />
            <Space direction="vertical">
                {displayedSideItems.map((sideItem, id) => {
                    const itemLabel =
                        isLaminateSelected && sideItem.sideType === SideItemType.WALL_SIDE ? 'Raw' : sideItem.value;

                    return <SideItemRadio key={id} sideItem={sideItem} shape={shape} label={itemLabel} />;
                })}
            </Space>
        </div>
    );
};
export default SideItemsRadioList;
